import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Shield, ShieldCheck, ShieldAlert, RefreshCw, Save, X, Users,
  LayoutDashboard, UserPlus, BookOpen, GraduationCap, Newspaper,
  Share2, Building, Wallet, Receipt, CalendarCheck, UserCog, Link, Package, Mail,
  User, Brain, FileText, Settings, MessageSquare, Bot, TrendingUp,
  Eye, Pencil, Ban, Boxes, FolderOpen, Megaphone, Camera, Phone, Bell, ClipboardList
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface PermissionDefinition {
  permission_key: string;
  category: string;
  name: string;
  description: string;
  risk_level: string;
}

interface AdminPermission {
  permission_key: string;
  resource_scope: string;
  expires_at: string | null;
  granted_at: string;
  granted_by: string | null;
}

interface PermissionGroup {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
}

interface PermissionEditorProps {
  adminId: string;
  adminName: string;
  onClose: () => void;
  onSave: () => void;
}

interface ModuleConfig {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  permissionPrefixes?: string[];
  alwaysAccessible?: boolean;
}

export const mainModules: ModuleConfig[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, permissionPrefixes: ["dashboard."] },
  { id: "training-reg", label: "AI Academy Registration", icon: UserPlus, permissionPrefixes: ["registrations."] },
  { id: "members", label: "HIBANE AI HUB Members", icon: Users, permissionPrefixes: ["members."] },
  { id: "trainings", label: "Paid Training", icon: BookOpen, permissionPrefixes: ["trainings."] },
  { id: "academy", label: "Paid Training Registrations", icon: GraduationCap, permissionPrefixes: ["applications."] },
  { id: "blog", label: "Blog", icon: Newspaper, permissionPrefixes: ["blog."] },
];

export const managementModules: ModuleConfig[] = [
  { id: "social-media", label: "Social Media", icon: Share2, permissionPrefixes: ["social_media."] },
  { id: "apartments", label: "Booking Management", icon: Building, permissionPrefixes: ["apartments."] },
  { id: "finance", label: "Finance", icon: Wallet, permissionPrefixes: ["invoices."] },
  { id: "consultations", label: "Consultations", icon: CalendarCheck, permissionPrefixes: ["consultations."] },
  { id: "admins", label: "Admins", icon: UserCog, permissionPrefixes: ["admins."] },
  { id: "training-links", label: "Training Links", icon: Link, permissionPrefixes: ["training_links."] },
  { id: "products", label: "Products", icon: Package, permissionPrefixes: ["products."] },
  { id: "trusted-clients", label: "Trusted Clients", icon: Building, permissionPrefixes: ["clients."] },
  { id: "email", label: "Email Templates", icon: Mail, permissionPrefixes: ["email."] },
  { id: "inventory", label: "Inventory", icon: Boxes, permissionPrefixes: ["inventory."] },
  { id: "file-manager", label: "File Manager", icon: FolderOpen, permissionPrefixes: ["files."] },
  { id: "newsletter", label: "Newsletter", icon: Megaphone, permissionPrefixes: ["newsletter."] },
  { id: "push-center", label: "Push Center", icon: Bell, permissionPrefixes: ["newsletter."] },
  { id: "media-bookings", label: "Media Bookings", icon: Camera, permissionPrefixes: ["media_bookings."] },
  { id: "voice-agent", label: "Voice Agent", icon: Phone, permissionPrefixes: ["voice_agent."] },
  { id: "hr", label: "HR Operations", icon: Users, permissionPrefixes: ["hr."] },
  { id: "forms", label: "Forms", icon: ClipboardList, permissionPrefixes: ["forms."] },
  { id: "reports", label: "Reports", icon: FileText, permissionPrefixes: ["reports."] },
  { id: "visitors", label: "Visitors & Kiosk", icon: Users, permissionPrefixes: ["visitors."] },
];

export const systemModules: ModuleConfig[] = [
  { id: "profile", label: "My Profile", icon: User, permissionPrefixes: ["profile."] },
  { id: "notifications", label: "Notifications", icon: Bell, permissionPrefixes: ["notifications."], alwaysAccessible: true },
  { id: "security", label: "Security", icon: Shield, permissionPrefixes: ["security."] },
  { id: "ai-hack-detection", label: "AI Hack Detection", icon: Brain, permissionPrefixes: ["security."] },
  { id: "admin-log", label: "Admin Log", icon: FileText, permissionPrefixes: ["admin_log."] },
  { id: "settings", label: "Settings", icon: Settings, permissionPrefixes: ["settings."] },
  { id: "messages", label: "Contact Messages", icon: MessageSquare, permissionPrefixes: ["messages."] },
  { id: "ai-concierge", label: "AI Concierge", icon: Bot, permissionPrefixes: ["ai_concierge."] },
  { id: "analytics", label: "Analytics", icon: TrendingUp, permissionPrefixes: ["analytics."] },
];

export type AccessLevel = "none" | "view" | "full";

export const VIEW_SUFFIXES = [".read", ".view", ".read_pii"];

export { type ModuleConfig };

export function PermissionEditor({ adminId, adminName, onClose, onSave }: PermissionEditorProps) {
  const [definitions, setDefinitions] = useState<PermissionDefinition[]>([]);
  const [currentPermissions, setCurrentPermissions] = useState<AdminPermission[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [adminId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: defData, error: defError } = await supabase
        .from('permission_definitions')
        .select('*')
        .order('category', { ascending: true });
      if (defError) throw defError;
      setDefinitions(defData || []);

      const { data: permData, error: permError } = await supabase
        .rpc('get_admin_permissions', { p_user_id: adminId });
      if (permError) throw permError;
      setCurrentPermissions(permData || []);
      const permSet = new Set((permData || []).map((p: AdminPermission) => p.permission_key));
      setSelectedPermissions(permSet);

      const { data: groupData, error: groupError } = await supabase
        .from('admin_permission_groups')
        .select('*')
        .order('name', { ascending: true });
      if (groupError) throw groupError;
      setGroups(groupData || []);
    } catch (error) {
      console.error('Error fetching permission data:', error);
      toast({ title: "Error", description: "Failed to load permission data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionKey: string) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionKey)) {
        newSet.delete(permissionKey);
      } else {
        newSet.add(permissionKey);
        if (permissionKey === '*') {
          newSet.clear();
          newSet.add('*');
        }
      }
      return newSet;
    });
  };

  const getModulePermissions = useCallback((mod: ModuleConfig): PermissionDefinition[] => {
    if (!mod.permissionPrefixes) return [];
    return definitions.filter(d =>
      d.permission_key !== '*' &&
      mod.permissionPrefixes!.some(prefix => d.permission_key.startsWith(prefix))
    );
  }, [definitions]);

  const isViewKey = (key: string) => VIEW_SUFFIXES.some(s => key.endsWith(s));

  const getModuleAccessLevel = useCallback((mod: ModuleConfig): AccessLevel => {
    const perms = getModulePermissions(mod);
    if (perms.length === 0) return "none";

    const viewKeys = perms.filter(p => isViewKey(p.permission_key));
    const allKeys = perms;

    const allSelected = allKeys.every(p => selectedPermissions.has(p.permission_key));
    if (allSelected) return "full";

    const hasAnyView = viewKeys.length > 0 && viewKeys.every(p => selectedPermissions.has(p.permission_key));
    const nonViewSelected = allKeys.filter(p => !isViewKey(p.permission_key) && selectedPermissions.has(p.permission_key));
    if (hasAnyView && nonViewSelected.length === 0) return "view";

    // If some permissions are selected but don't match view-only pattern, treat as partial → show "full" if all non-view are also selected
    const anySelected = allKeys.some(p => selectedPermissions.has(p.permission_key));
    if (anySelected) {
      // Partial selection — show as view if only view keys, otherwise none (user must re-select)
      const onlyViewSelected = allKeys.filter(p => selectedPermissions.has(p.permission_key)).every(p => isViewKey(p.permission_key));
      if (onlyViewSelected) return "view";
    }

    return "none";
  }, [getModulePermissions, selectedPermissions]);

  const setModuleAccessLevel = (mod: ModuleConfig, level: AccessLevel) => {
    const perms = getModulePermissions(mod);
    if (perms.length === 0) return;

    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      // Remove all module permissions first
      perms.forEach(p => newSet.delete(p.permission_key));

      if (level === "view") {
        perms.filter(p => isViewKey(p.permission_key)).forEach(p => newSet.add(p.permission_key));
      } else if (level === "full") {
        perms.forEach(p => newSet.add(p.permission_key));
      }
      return newSet;
    });
  };

  const selectAllModules = () => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      [...mainModules, ...managementModules, ...systemModules].forEach(mod => {
        getModulePermissions(mod).forEach(p => newSet.add(p.permission_key));
      });
      return newSet;
    });
  };

  const deselectAllModules = () => {
    setSelectedPermissions(new Set());
  };

  const handleGroupAssign = async (groupName: string) => {
    const group = groups.find(g => g.name === groupName);
    if (!group) return;
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      group.permissions.forEach(p => newSet.add(p));
      return newSet;
    });
    toast({ title: "Group Applied", description: `${group.name} permissions selected. Click Save to apply.` });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const currentKeys = new Set(currentPermissions.map(p => p.permission_key));
      const toAdd = [...selectedPermissions].filter(p => !currentKeys.has(p));
      const toRemove = [...currentKeys].filter(p => !selectedPermissions.has(p));

      for (const permKey of toRemove) {
        const { error } = await supabase.rpc('revoke_admin_permission', {
          p_user_id: adminId, p_permission_key: permKey, p_revoked_by: user.id, p_resource_scope: '*'
        });
        if (error) throw error;
      }

      for (const permKey of toAdd) {
        const { error } = await supabase.rpc('grant_admin_permission', {
          p_user_id: adminId, p_permission_key: permKey, p_granted_by: user.id,
          p_resource_scope: '*', p_expires_at: null, p_reason: null
        });
        if (error) throw error;
      }

      toast({ title: "Permissions Updated", description: `Successfully updated permissions for ${adminName}` });
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast({ title: "Error", description: "Failed to save permissions", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const hasSuperAdmin = selectedPermissions.has('*');
  const changesCount = (() => {
    const currentKeys = new Set(currentPermissions.map(p => p.permission_key));
    const added = [...selectedPermissions].filter(p => !currentKeys.has(p)).length;
    const removed = [...currentKeys].filter(p => !selectedPermissions.has(p)).length;
    return added + removed;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const renderModuleRow = (mod: ModuleConfig) => {
    const Icon = mod.icon;

    if (mod.alwaysAccessible) {
      return (
        <div key={mod.id} className="flex items-center justify-between py-3 px-4 rounded-lg border border-border/50 bg-muted/30">
          <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">{mod.label}</span>
          </div>
          <Badge variant="outline" className="text-xs text-muted-foreground">Always accessible</Badge>
        </div>
      );
    }

    const perms = getModulePermissions(mod);
    if (perms.length === 0) return null;

    const level = getModuleAccessLevel(mod);
    const viewKeys = perms.filter(p => isViewKey(p.permission_key));
    const hasViewKeys = viewKeys.length > 0;

    return (
      <div key={mod.id} className="flex items-center justify-between py-3 px-4 rounded-lg border border-border/50 hover:bg-muted/20 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium text-sm truncate">{mod.label}</span>
        </div>
        <ToggleGroup
          type="single"
          value={level}
          onValueChange={(val) => {
            setModuleAccessLevel(mod, (val || "none") as AccessLevel);
          }}
          className="shrink-0"
        >
          <ToggleGroupItem
            value="none"
            aria-label="No Access"
            className={cn(
              "text-xs px-2.5 h-8 gap-1 data-[state=on]:bg-muted data-[state=on]:text-muted-foreground",
            )}
          >
            <Ban className="h-3 w-3" />
            <span className="hidden sm:inline">None</span>
          </ToggleGroupItem>
          {hasViewKeys && (
            <ToggleGroupItem
              value="view"
              aria-label="View Only"
              className={cn(
                "text-xs px-2.5 h-8 gap-1 data-[state=on]:bg-blue-500/15 data-[state=on]:text-blue-600 dark:data-[state=on]:text-blue-400",
              )}
            >
              <Eye className="h-3 w-3" />
              <span className="hidden sm:inline">View</span>
            </ToggleGroupItem>
          )}
          <ToggleGroupItem
            value="full"
            aria-label="Full Access"
            className={cn(
              "text-xs px-2.5 h-8 gap-1 data-[state=on]:bg-[#1E5C95]/15 data-[state=on]:text-[#1E5C95] dark:data-[state=on]:text-[#1E5C95]",
            )}
          >
            <Pencil className="h-3 w-3" />
            <span className="hidden sm:inline">Full</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    );
  };

  const renderSection = (title: string, modules: ModuleConfig[]) => {
    const hasModules = modules.some(m => m.alwaysAccessible || getModulePermissions(m).length > 0);
    if (!hasModules) return null;

    return (
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">{title}</h4>
        <div className="space-y-1.5">
          {modules.map(mod => {
            if (!mod.alwaysAccessible && getModulePermissions(mod).length === 0) return null;
            return renderModuleRow(mod);
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Edit Permissions for {adminName}</h3>
          <p className="text-sm text-muted-foreground">
            {selectedPermissions.size} permissions selected
            {changesCount > 0 && (
              <Badge variant="secondary" className="ml-2">{changesCount} changes pending</Badge>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || changesCount === 0}>
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Quick Assign Groups */}
      {groups.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Quick Assign Permission Group
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select onValueChange={handleGroupAssign}>
              <SelectTrigger>
                <SelectValue placeholder="Select a permission group to apply..." />
              </SelectTrigger>
              <SelectContent>
                {groups.map(group => (
                  <SelectItem key={group.id} value={group.name}>
                    <div className="flex flex-col">
                      <span>{group.name}</span>
                      {group.description && <span className="text-xs text-muted-foreground">{group.description}</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Super Admin Toggle */}
      <Card className={cn(hasSuperAdmin && "border-destructive")}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                id="super-admin"
                checked={hasSuperAdmin}
                onCheckedChange={() => handlePermissionToggle('*')}
              />
              <div>
                <Label htmlFor="super-admin" className="text-base font-semibold flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-destructive" />
                  Super Admin
                </Label>
                <p className="text-sm text-muted-foreground">
                  Full access to all features and data. Overrides all other permissions.
                </p>
              </div>
            </div>
            <Badge variant="destructive">CRITICAL</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Module-based permissions */}
      {!hasSuperAdmin && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAllModules}>Select All Modules</Button>
            <Button variant="outline" size="sm" onClick={deselectAllModules}>Deselect All</Button>
          </div>

          {renderSection("Main", mainModules)}
          {renderSection("Management", managementModules)}
          {renderSection("System", systemModules)}
        </div>
      )}

      {hasSuperAdmin && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-muted-foreground">
            <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Super Admin has full access to all permissions.</p>
            <p className="text-sm">Individual permissions are hidden when Super Admin is enabled.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PermissionEditor;
